import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Package, X } from 'lucide-react';
import { AvailableProject } from '../../types/delivery.types';
import { projectService } from '../../services/projectService';
import DataTable, { Column } from '../ui/DataTable';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ProjectSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProjectsSelect: (projects: AvailableProject[]) => void;
    selectedTaskTitle?: string;
    excludeProjectIds?: number[];
}

export default function ProjectSelectionModal({
    isOpen,
    onClose,
    onProjectsSelect,
    selectedTaskTitle,
    excludeProjectIds = []
}: ProjectSelectionModalProps) {
    const [projects, setProjects] = useState<AvailableProject[]>([]);
    const [allProjects, setAllProjects] = useState<AvailableProject[]>([]);
    const [paginationData, setPaginationData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedProjects, setSelectedProjects] = useState<Set<number>>(new Set());

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [filters, setFilters] = useState<Record<string, string>>({});

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const searchFilters: Record<string, string> = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value.toString().trim() !== '') {
                    searchFilters[key] = value.toString();
                }
            });

            const response = await projectService.getAllPaginated({
                page,
                size,
                sort: [{ field: sortField, direction: sortDirection }],
                filters: searchFilters
            });

            const availableProjects: AvailableProject[] = (response.content || [])
                .map(project => ({
                    id: project.id,
                    name: project.name,
                    repositoryUrl: project.repositoryUrl
                }))
                .filter(project => !excludeProjectIds.includes(project.id));

            setProjects(availableProjects);
            setPaginationData(response);

            if (page === 0) {
                try {
                    const allResponse = await projectService.getAll();
                    const allAvailableProjects = (allResponse.content || [])
                        .map(project => ({
                            id: project.id,
                            name: project.name,
                            repositoryUrl: project.repositoryUrl
                        }))
                        .filter(project => !excludeProjectIds.includes(project.id));
                    setAllProjects(allAvailableProjects);
                } catch (e) {
                    console.warn('Não foi possível carregar todos os projetos:', e);
                    setAllProjects(availableProjects);
                }
            }
        } catch (error) {
            console.error('Erro ao buscar projetos:', error);
            setProjects([]);
            setPaginationData(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        const isImmediateChange = Object.keys(filters).length === 0;

        if (isImmediateChange) {
            fetchProjects();
        } else {
            debounceTimerRef.current = setTimeout(() => {
                fetchProjects();
            }, 800);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [isOpen, page, size, sortField, sortDirection, filters]);

    useEffect(() => {
        if (isOpen) {
            setSelectedProjects(new Set());
            setPage(0);
            setFilters({});

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const toggleProjectSelection = (projectId: number) => {
        const newSelected = new Set(selectedProjects);
        if (newSelected.has(projectId)) {
            newSelected.delete(projectId);
        } else {
            newSelected.add(projectId);
        }
        setSelectedProjects(newSelected);
    };

    const handleRowClick = (project: AvailableProject) => {
        toggleProjectSelection(project.id);
    };

    const toggleSelectAll = () => {
        const newSelected = new Set(selectedProjects);

        const allCurrentPageSelected = projects.every(project => selectedProjects.has(project.id));

        if (allCurrentPageSelected && projects.length > 0) {
            projects.forEach(project => {
                newSelected.delete(project.id);
            });
        } else {
            projects.forEach(project => {
                newSelected.add(project.id);
            });
        }

        setSelectedProjects(newSelected);
    };

    const handleConfirmSelection = () => {
        const selectedProjectsList = allProjects.filter(project => 
            selectedProjects.has(project.id)
        );
        onProjectsSelect(selectedProjectsList);
        onClose();
    };

    const columns: Column<AvailableProject>[] = [
        {
            key: 'selected',
            title: 'Selecionar',
            sortable: false,
            filterable: false,
            headerRender: () => (
                <div className="flex items-center justify-center" title="Selecionar todos desta página">
                    <input
                        type="checkbox"
                        checked={projects.length > 0 && projects.every(project => selectedProjects.has(project.id))}
                        onChange={toggleSelectAll}
                        className="rounded border-border-strong text-accent focus:ring-accent"
                    />
                </div>
            ),
            render: (project) => (
                <div className="flex items-center justify-center">
                    <input
                        type="checkbox"
                        checked={selectedProjects.has(project.id)}
                        onChange={() => toggleProjectSelection(project.id)}
                        className="rounded border-border-strong text-accent focus:ring-accent"
                    />
                </div>
            ),
            width: '60px'
        },
        {
            key: 'name',
            title: 'Nome do Projeto',
            sortable: true,
            filterable: true,
            render: (project) => (
                <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-text-tertiary" />
                    <span className="font-medium text-text-primary truncate" title={project.name}>
                        {project.name}
                    </span>
                </div>
            ),
            width: '100%'
        }
    ];

    if (!isOpen) return null;

    const selectedCount = selectedProjects.size;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-surface-1 rounded-lg shadow-xl w-full max-w-full sm:max-w-4xl max-h-[92dvh] sm:max-h-[85vh] overflow-hidden">
                <div className="px-4 sm:px-6 py-3 border-b border-border-subtle bg-surface-1 sticky top-0">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <h2 className="text-base sm:text-xl font-semibold text-text-primary truncate pr-2">
                                Selecionar Repositórios/Projetos
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-text-tertiary hover:text-text-secondary transition-colors p-1 flex-shrink-0"
                            title="Fechar (ESC)"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {selectedCount > 0 && (
                        <div className="mt-3 text-sm">
                            <span className="font-medium text-accent">{selectedCount}</span>
                            {' '}projeto{selectedCount !== 1 ? 's' : ''} selecionado{selectedCount !== 1 ? 's' : ''}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden max-h-[calc(92dvh-140px)] sm:max-h-[calc(85vh-140px)]">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <LoadingSpinner size="lg" />
                            <span className="ml-3 text-text-secondary">Carregando projetos...</span>
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Package className="mx-auto h-12 w-12 text-text-tertiary" />
                                <h3 className="mt-2 text-sm font-medium text-text-primary">
                                    Nenhum projeto encontrado
                                </h3>
                                <p className="mt-1 text-sm text-text-tertiary">
                                    Cadastre projetos para poder selecioná-los
                                </p>
                            </div>
                        </div>
                    ) : (
                        <DataTable
                            data={projects}
                            columns={columns}
                            onRowClick={handleRowClick}
                            selectedRows={Array.from(selectedProjects)}
                            loading={isLoading}
                            showColumnToggle={false}

                            pagination={paginationData ? {
                                currentPage: page,
                                pageSize: size,
                                totalElements: paginationData.totalElements,
                                totalPages: paginationData.totalPages
                            } : null}
                            onPageChange={setPage}
                            onPageSizeChange={setSize}

                            sorting={[{ field: sortField, direction: sortDirection }]}
                            onSort={(field, direction) => {
                                setSortField(field);
                                setSortDirection(direction);
                                setPage(0);
                            }}

                            filters={filters}
                            onFilter={(field, value) => {
                                setFilters(prev => ({
                                    ...prev,
                                    [field]: value || undefined
                                }));
                                setPage(0);
                            }}
                            onClearFilters={() => {
                                setFilters({});
                                setPage(0);
                            }}
                        />
                    )}
                </div>

                <div className="px-3 sm:px-6 py-2 sm:py-1.5 border-t border-border-subtle bg-surface-1 flex items-center justify-between gap-3">
                    <div className="min-w-0 text-xs sm:text-sm text-text-secondary">
                        {paginationData?.totalElements || 0} projeto{(paginationData?.totalElements || 0) !== 1 ? 's' : ''} disponível{(paginationData?.totalElements || 0) !== 1 ? 'eis' : ''}
                        {selectedCount > 0 && (
                            <span className="ml-2 font-medium text-accent">
                                • {selectedCount} selecionado{selectedCount !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    
                    <div className="flex justify-center shrink-0">
                        <Button
                            onClick={handleConfirmSelection}
                        >
                            Selecionar
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}